import i18n from "@/core/i18n";
import {
  selectCameraApiCredentials,
  selectCameraApiUrl,
} from "@/modules/project/services/projectSlice";
import { RootState } from "@/store";
import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
} from "@reduxjs/toolkit/query";

export type CameraApiExtraOptions = { timeoutMs?: number };

/**
 * Dynamic base query that resolves the camera API URL from the active project.
 */
export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  CameraApiExtraOptions
> = async (args, api, extraOptions) => {
  const state = api.getState() as RootState;
  const baseUrl = selectCameraApiUrl(state);

  // No base URL means the camera is not addressable yet — most often a
  // relay-backed project whose session cookie is still being minted. Failing
  // here rather than falling back to "" is the whole point: an empty base
  // resolves a camera path against the *web app's* origin, where the SPA
  // fallback answers every unknown path with index.html and HTTP 200. That
  // turned "too early" into a successful-looking response carrying HTML, and
  // RTK Query cached it — the streams query never recovered and the Capture
  // page sat on "Searching for camera..." forever.
  //
  // Call sites should still gate on the URL so the query starts cleanly once
  // it appears; this is the backstop that makes a missed gate loud instead of
  // silent.
  if (!baseUrl) {
    return {
      error: {
        status: "CUSTOM_ERROR",
        error: "CAMERA_API_UNAVAILABLE",
        data: { message: i18n.t("camera:api.noUrl") },
      },
    };
  }

  const dynamicBaseQuery = fetchBaseQuery({
    // Credentials only on the relay path — same rule as before, now shared with
    // the raw `fetch` call sites via the selector so they cannot disagree. The
    // relay needs its origin cookie or every request 401s; a direct LAN camera
    // must NOT get one, because its wildcard `Access-Control-Allow-Origin` is
    // refused for credentialed requests. See `selectCameraApiCredentials`.
    credentials: selectCameraApiCredentials(state),
    baseUrl,
    prepareHeaders: (headers) => {
      const contentType = headers.get("Content-Type");
      if (!contentType) {
        headers.set("Content-Type", "application/json");
      }
      return headers;
    },
  });

  const { timeoutMs, ...remainingOptions } = extraOptions ?? {};

  if (!timeoutMs) {
    return dynamicBaseQuery(args, api, remainingOptions);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  api.signal.addEventListener("abort", () => controller.abort());

  const argsWithSignal: FetchArgs =
    typeof args === "string"
      ? { url: args, signal: controller.signal }
      : { ...args, signal: controller.signal };

  try {
    return await dynamicBaseQuery(argsWithSignal, api, remainingOptions);
  } finally {
    clearTimeout(timeoutId);
  }
};
